import { Directive, computed, input } from '@angular/core';

export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-md font-semibold ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ' +
  // A disabled button drops its fill entirely rather than fading it: at 50% opacity the
  // teal still read as the primary action on a full class.
  'disabled:bg-surface-raised disabled:text-fg-subtle disabled:inset-ring-0 ' +
  'disabled:cursor-not-allowed disabled:pointer-events-none transition-colors';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-fg hover:bg-brand-hover',
  secondary:
    'bg-surface-raised text-fg inset-ring inset-ring-border hover:bg-surface-card',
  soft: 'bg-brand/15 text-brand hover:bg-brand/25',
  danger: 'bg-danger text-fg hover:bg-danger/85',
};

// Two floors, because the right answer differs by input device: `pointer-coarse` is a
// finger and gets 44px (WCAG 2.5.5) even on the small size, a mouse gets the compact
// height. Without the coarse rule `sm` renders 36px, which is a miss on a phone; with a
// flat 44px everywhere the desktop tables look inflated.
const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-9 pointer-coarse:min-h-11 px-3 py-1.5 text-sm',
  md: 'min-h-11 px-4 py-2 text-sm',
  lg: 'min-h-12 px-5 py-2.5 text-base',
};

/**
 * A directive, not a component, so the host stays a real <button>: type="submit", form
 * association, [disabled] and (click) all keep working, and no wrapper element lands in
 * the middle of a flex or grid layout.
 */
@Directive({
  selector: 'button[appButton], a[appButton]',
  host: { '[class]': 'classes()' },
})
export class ButtonDirective {
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('md');

  protected readonly classes = computed(
    () => `${BASE} ${VARIANT[this.variant()]} ${SIZE[this.size()]}`,
  );
}
